<template>
  <div class="vista-general-produccion">
    <main-breadcrum
      :items="breadcrumItems"
      show-back-button
    />
    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title
          >Cálculos Productivos
          {{ codigoCotizacion ? ` - Cotización #${codigoCotizacion}` : '' }}</custom-title
        >
      </template>
      <template #right>
        <el-button
          type="info"
          plain
          @click="volver"
        >
          <IconArrowLeft class="mr-1" /> Volver
        </el-button>
      </template>
    </main-buttons-bar>

    <div class="contenido-principal">
      <loading-overlay :show="store.loading" />

      <template v-if="codigoCotizacion">
        <banner-stale :is-stale="store.isStale" />

        <div
          v-if="store.fechaCalculo"
          class="header-info"
        >
          <span class="fecha-calculo">
            <IconClockOutline class="fecha-icon" />
            Calculado: {{ formatearFecha(store.fechaCalculo) }}
          </span>
        </div>

        <selector-cantidad
          v-if="store.tieneMultiplesCantidades"
          v-model="cantidadSeleccionada"
          :cantidades="store.cantidades"
          :disabled="store.isStale"
          @update:model-value="handleCantidadChange"
        />

        <template v-if="store.resultadoDetalle">
          <datos-pliego
            :producto="store.datosProducto"
            :is-stale="store.isStale"
          />

          <cantidades-netas
            :cantidad-datos="store.datosCantidad"
            :producto="store.datosProducto"
            :is-stale="store.isStale"
          />

          <lista-procesos
            :procesos="store.procesos"
            :is-stale="store.isStale"
          />
        </template>

        <div
          v-if="store.error"
          class="error-container"
        >
          <el-alert
            :title="store.error"
            type="error"
            show-icon
            :closable="false"
          />
        </div>

        <div
          v-if="!store.loading && !store.resultadoDetalle && !store.error"
          class="empty-state"
        >
          <IconInformationOutline class="empty-icon" />
          <p>No hay datos de producción disponibles para esta cotización.</p>
          <p class="empty-hint">
            Presione "Cotizar" en la pantalla de edición para generar los cálculos.
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import MainBreadcrum from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import LoadingOverlay from '@/shared/LoadingOverlay.vue';
import IconArrowLeft from '~icons/mdi/arrow-left';
import IconClockOutline from '~icons/mdi/clock-outline';
import IconInformationOutline from '~icons/mdi/information-outline';
import { formatearFecha } from '@/shared/utils/format_produccion.js';
import { useResultadoProduccionStore } from './stores/resultado_produccion_store.js';
import BannerStale from './components/banner_stale.vue';
import SelectorCantidad from './components/selector_cantidad.vue';
import DatosPliego from './components/datos_pliego.vue';
import CantidadesNetas from './components/cantidades_netas.vue';
import ListaProcesos from './components/lista_procesos.vue';

const route = useRoute();
const router = useRouter();
const store = useResultadoProduccionStore();

const codigoCotizacion = computed(() => route.params.codigo);

const breadcrumItems = computed(() => {
  const items = [{ label: 'Cotizaciones', path: '/page/prices/list' }];
  if (codigoCotizacion.value) {
    items.push({
      label: `Cotización #${codigoCotizacion.value}`,
      path: `/page/prices/edit/${codigoCotizacion.value}`,
    });
  }
  items.push({ label: 'Cálculos Productivos' });
  return items;
});

const cantidadSeleccionada = computed({
  get: () => store.cantidadSeleccionada,
  set: () => {},
});

function handleCantidadChange(codigoCantidad) {
  store.fetchResultadoPorCantidadHTTP(codigoCotizacion.value, codigoCantidad);
}

function volver() {
  router.push(`/page/prices/edit/${codigoCotizacion.value}`);
}

onMounted(async () => {
  if (codigoCotizacion.value) {
    store.fetchResultadosHTTP(codigoCotizacion.value);
  }
});

onBeforeUnmount(() => {
  store.reset();
});

watch(codigoCotizacion, (nuevoValor) => {
  if (nuevoValor) {
    store.reset();
    store.fetchResultadosHTTP(nuevoValor);
  }
});
</script>

<style scoped>
.vista-general-produccion {
  min-height: 100%;
  overflow-y: auto;
}

.contenido-principal {
  position: relative;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 20px;
  min-height: 400px;
}

.header-info {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.fecha-calculo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #606266;
  background: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.fecha-icon {
  font-size: 16px;
  color: #909399;
}

.error-container {
  margin-top: 16px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  color: #c0c4cc;
}

.empty-state p {
  margin: 8px 0;
  font-size: 14px;
}

.empty-hint {
  font-size: 12px !important;
  color: #c0c4cc;
}
</style>
